import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';
import type { ViewType } from '../data/homepageData';
import { CREATOR_STATS, BRAND_STATS } from '../data/homepageData';

interface StatsBarProps {
  view: ViewType;
}

/**
 * Stats bar showing 4 key metrics that switch between creator and brand data.
 * Uses framer-motion whileInView for scroll reveal animation.
 */
const StatsBar: React.FC<StatsBarProps> = ({ view }) => {
  const stats = view === 'creator' ? CREATOR_STATS : BRAND_STATS;

  return (
    <section
      className="py-10"
      style={{
        borderTop: '1px solid var(--cv-border)',
        borderBottom: '1px solid var(--cv-border)',
        background: 'var(--cv-surface)',
      }}
    >
      <div className="w-full max-w-[1200px] mx-auto px-6">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 md:gap-8 text-center">
          {stats.map((stat, i) => {
            const isMoneyValue = stat.number.includes('$');

            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{
                  duration: 0.7,
                  delay: i * 0.1,
                  ease: [0.16, 1, 0.3, 1],
                }}
              >
                <div
                  className={cn(
                    'font-extrabold tracking-tight mb-1',
                    isMoneyValue ? 'cv-text-gradient-green' : 'cv-text-gradient',
                  )}
                  style={{
                    fontFamily: 'var(--font-display)',
                    fontSize: 'var(--text-2xl)',
                    letterSpacing: '-0.02em',
                  }}
                >
                  {stat.number}
                </div>
                <div
                  className="uppercase font-medium"
                  style={{
                    fontSize: 'var(--text-xs)',
                    color: 'var(--cv-text-muted)',
                    letterSpacing: '0.08em',
                  }}
                >
                  {stat.label}
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default StatsBar;
